/**
 * TemplateSelectionGallery
 *
 * A modal for selecting a template, optionally with module name input.
 * Used for both:
 * 1. "Add Linking Module" flow - with module name input at top
 * 2. Template selection during edit - without module name input
 *
 * Usage for new module:
 *   <TemplateSelectionGallery
 *     open={true}
 *     onClose={handleClose}
 *     onSelect={handleSelect}
 *     showModuleName={true}
 *     moduleName={name}
 *     onModuleNameChange={setName}
 *   />
 *
 * Usage for template selection only:
 *   <TemplateSelectionGallery
 *     open={true}
 *     onClose={handleClose}
 *     onSelect={handleSelect}
 *   />
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import {
  TemplateType,
  TemplateCategory,
  TEMPLATE_CATEGORIES,
  getTemplatesByCategory,
  getDefaultConfig,
  getTemplateById,
} from './types';
import TemplatePreviewRenderer from './TemplatePreviewRenderer';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '1000px',
      width: '90vw',
      height: '85vh',
      maxHeight: '85vh',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 8,
    },
  },
  dialogTitleWrapper: {
    position: 'relative',
    borderBottom: '1px solid #e5e7eb',
    padding: theme.spacing(2, 3),
    paddingRight: theme.spacing(6),
    flexShrink: 0,
    '& .MuiTypography-root': {
      margin: 0,
      fontWeight: 600,
      color: '#1e3a5f',
    },
  },
  closeIcon: {
    position: 'absolute',
    top: 16,
    right: 16,
    cursor: 'pointer',
    color: '#6b7280',
    '&:hover': {
      color: '#374151',
    },
  },
  dialogContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    flex: 1,
  },
  moduleNameSection: {
    padding: theme.spacing(2.5, 3),
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#fafafa',
    flexShrink: 0,
  },
  moduleNameLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1e3a5f',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  moduleNameInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
    },
  },
  moduleNameError: {
    color: '#ef4444',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  categoryTabs: {
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#fff',
    flexShrink: 0,
    '& .MuiTab-root': {
      textTransform: 'none',
      minWidth: 'auto',
      padding: theme.spacing(1.5, 2),
      fontSize: '0.875rem',
    },
    '& .Mui-selected': {
      color: '#1e3a5f',
      fontWeight: 600,
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#3ecf8e',
    },
  },
  galleryContainer: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2.5, 3),
  },
  galleryHeader: {
    marginBottom: theme.spacing(2),
  },
  templateSectionLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1e3a5f',
    marginBottom: theme.spacing(0.5),
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: theme.spacing(2),
  },
  templateCard: {
    border: '2px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#3ecf8e',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      transform: 'translateY(-2px)',
    },
  },
  templateCardSelected: {
    borderColor: '#3ecf8e',
    boxShadow: '0 0 0 3px rgba(62, 207, 142, 0.2)',
  },
  templatePreview: {
    height: 160,
    backgroundColor: '#f9fafb',
    padding: theme.spacing(1.5),
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  templatePreviewContent: {
    transform: 'scale(0.75)',
    transformOrigin: 'top center',
    width: '100%',
  },
  templateCardHeader: {
    padding: theme.spacing(1.5, 2),
    borderTop: '1px solid #e5e7eb',
  },
  templateTags: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '10px',
    height: 20,
    backgroundColor: '#e5e7eb',
    color: '#4b5563',
  },
  checkIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#3ecf8e',
    color: 'white',
    borderRadius: '50%',
    padding: 4,
    width: 24,
    height: 24,
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: '1px solid #e5e7eb',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  skipLink: {
    color: '#6b7280',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  optionalBadge: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    fontWeight: 400,
    marginLeft: theme.spacing(1),
  },
}));

interface TemplateSelectionGalleryProps {
  open: boolean;
  onClose: () => void;
  /** Callback when user confirms selection. Returns templateId (null if skipped) and moduleName (if shown) */
  onSelect: (templateId: TemplateType | null, moduleName?: string) => void;

  // Module name input options (for new module creation)
  /** Show module name input field at top */
  showModuleName?: boolean;
  /** Current module name value */
  moduleName?: string;
  /** Callback when module name changes */
  onModuleNameChange?: (name: string) => void;
  /** Module name validation error */
  moduleNameError?: string;
  /** Placeholder for module name input */
  moduleNamePlaceholder?: string;

  // Template selection options
  /** Allow skipping template selection */
  allowSkip?: boolean;
  /** Callback when user skips (alternative to onSelect with null) */
  onSkip?: () => void;
  /** Pre-selected template */
  initialTemplate?: TemplateType | null;
  /** Custom title for the modal */
  title?: string;
  /** Custom button text */
  confirmButtonText?: string;
}

const TemplateSelectionGallery: React.FC<TemplateSelectionGalleryProps> = ({
  open,
  onClose,
  onSelect,
  showModuleName = false,
  moduleName: externalModuleName,
  onModuleNameChange,
  moduleNameError,
  moduleNamePlaceholder = 'e.g., Related Articles Module',
  allowSkip = false,
  onSkip,
  initialTemplate = null,
  title,
  confirmButtonText,
}) => {
  const classes = useStyles();
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | null>(initialTemplate);
  const [internalModuleName, setInternalModuleName] = useState('');

  // Use external or internal module name
  const moduleName = externalModuleName !== undefined ? externalModuleName : internalModuleName;
  const setModuleName = onModuleNameChange || setInternalModuleName;

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedTemplate(initialTemplate);
      setActiveCategory('all');
      if (!externalModuleName) {
        setInternalModuleName('');
      }
    }
  }, [open, initialTemplate, externalModuleName]);

  // Auto-suggest module name when template is selected (only if name is empty)
  useEffect(() => {
    if (showModuleName && selectedTemplate && !moduleName) {
      const templateDef = getTemplateById(selectedTemplate);
      if (templateDef) {
        setModuleName(`${templateDef.name} Module`);
      }
    }
  }, [selectedTemplate, showModuleName, moduleName, setModuleName]);

  // Get filtered templates
  const filteredTemplates = useMemo(() => {
    return getTemplatesByCategory(activeCategory);
  }, [activeCategory]);

  // Determine modal title
  const modalTitle = title || (showModuleName ? 'Create New Linking Module' : 'Choose a Template');

  // Determine button text
  const buttonText = confirmButtonText || (showModuleName ? 'Create Module' : 'Select Template');

  // Check if form is valid
  const isValid = useMemo(() => {
    if (showModuleName) {
      // For new module: need name, template is optional
      return moduleName.trim().length > 0 && !moduleNameError;
    }
    // For template selection only: need template
    return selectedTemplate !== null;
  }, [showModuleName, moduleName, moduleNameError, selectedTemplate]);

  // Handle template click
  const handleTemplateClick = (templateId: TemplateType) => {
    setSelectedTemplate(templateId);
  };

  // Handle double-click for quick selection
  const handleTemplateDoubleClick = (templateId: TemplateType) => {
    if (!showModuleName || (moduleName.trim().length > 0 && !moduleNameError)) {
      setSelectedTemplate(templateId);
      handleConfirm(templateId);
    }
  };

  // Handle confirm button
  const handleConfirm = (overrideTemplate?: TemplateType) => {
    const template = overrideTemplate || selectedTemplate;
    onSelect(template, showModuleName ? moduleName : undefined);
    handleClose();
  };

  // Handle skip
  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onSelect(null, showModuleName ? moduleName : undefined);
    }
    handleClose();
  };

  // Reset state on close
  const handleClose = () => {
    setSelectedTemplate(null);
    setActiveCategory('all');
    setInternalModuleName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog} maxWidth={false}>
      <DialogTitle className={classes.dialogTitleWrapper} disableTypography>
        <Typography variant="h6">{modalTitle}</Typography>
        <CloseIcon onClick={handleClose} className={classes.closeIcon} />
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Module Name Input Section */}
        {showModuleName && (
          <Box className={classes.moduleNameSection}>
            <label className={classes.moduleNameLabel}>Module Name</label>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={moduleName}
              onChange={(e) => setModuleName(e.target.value)}
              placeholder={moduleNamePlaceholder}
              error={!!moduleNameError}
              className={classes.moduleNameInput}
            />
            {moduleNameError && <Typography className={classes.moduleNameError}>{moduleNameError}</Typography>}
          </Box>
        )}

        {/* Template Selection Section */}
        <Box className={classes.galleryHeader} style={{ padding: '16px 24px 0', marginBottom: 0 }}>
          <Typography className={classes.templateSectionLabel}>
            Choose a Template
            {showModuleName && <span className={classes.optionalBadge}>(Optional - you can select later)</span>}
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>
            Select how you want your internal links to appear.
          </Typography>
        </Box>

        <Tabs
          value={activeCategory}
          onChange={(_, val) => setActiveCategory(val)}
          className={classes.categoryTabs}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TEMPLATE_CATEGORIES.map((cat) => (
            <Tab key={cat.id} value={cat.id} label={cat.label} />
          ))}
        </Tabs>

        <Box className={classes.galleryContainer}>
          <Box className={classes.templateGrid}>
            {filteredTemplates.map((template) => {
              const isSelected = selectedTemplate === template.id;
              return (
                <Box
                  key={template.id}
                  className={`${classes.templateCard} ${isSelected ? classes.templateCardSelected : ''}`}
                  onClick={() => handleTemplateClick(template.id)}
                  onDoubleClick={() => handleTemplateDoubleClick(template.id)}
                >
                  <Box className={classes.templatePreview}>
                    <Box className={classes.templatePreviewContent}>
                      <TemplatePreviewRenderer
                        templateId={template.id}
                        config={getDefaultConfig(template.id)}
                        isGalleryPreview
                      />
                    </Box>
                    {isSelected && <CheckIcon className={classes.checkIcon} />}
                  </Box>
                  <Box className={classes.templateCardHeader}>
                    <Typography variant="body2" style={{ fontWeight: 600, color: '#1e3a5f', fontSize: '0.85rem' }}>
                      {template.name}
                    </Typography>
                    <Box className={classes.templateTags}>
                      {template.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" className={classes.tag} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className={`${classes.dialogActions} buttons-inline`}>
        {/* Left side - skip link */}
        {allowSkip && showModuleName && moduleName.trim().length > 0 && (
          <Button onClick={handleSkip} className={classes.skipLink}>
            Skip template selection
          </Button>
        )}

        {/* Spacer */}
        <Box flex={1} />

        {/* Right side - Cancel and Primary */}
        <Button
          onClick={handleClose}
          color="primary"
          className="internal-linking-advance-filters-cancel-btn"
          style={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleConfirm()}
          variant="contained"
          color="primary"
          disabled={!isValid}
          className="primary-button font-500-ft12-lh16 text-white text-title-case radius-0 no-box-shadow"
          style={{ textTransform: 'none' }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionGallery;
